import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Coins, CheckCircle2 } from "lucide-react";
import api, { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

export default function Checkout() {
  const { items, clear } = useCart();
  const navigate = useNavigate();
  const [usePoints, setUsePoints] = useState(true);
  const [quote, setQuote] = useState(null);
  const [address, setAddress] = useState("");
  const [payment, setPayment] = useState("cod");
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);

  const payload = items.map((i) => ({ product_id: i.product.product_id, quantity: i.quantity }));

  const refresh = useCallback(async () => {
    if (items.length === 0) return;
    try {
      const { data } = await api.post("/orders/quote", { items: payload, use_points: usePoints });
      setQuote(data);
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usePoints, items.length]);

  useEffect(() => { refresh(); }, [refresh]);

  if (items.length === 0 && !done)
    return <div className="mx-auto max-w-2xl px-4 py-20 text-center text-muted-foreground">Your cart is empty.</div>;

  if (done)
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center" data-testid="order-success">
        <CheckCircle2 className="mx-auto text-[hsl(var(--secondary))]" size={56} />
        <h1 className="font-heading font-extrabold text-3xl mt-4">Order Confirmed!</h1>
        <p className="text-muted-foreground mt-2">Order <b className="font-mono-points">#{done.order_id}</b> placed successfully.</p>
        <div className="mt-6 bg-card border border-border rounded-xl p-5 text-left space-y-2">
          <Row label="Subtotal" value={money(done.subtotal)} />
          {done.points_used > 0 && <Row label="RicePoints applied" value={`- ${money(done.points_used)}`} accent />}
          <Row label="Amount payable" value={money(done.payable)} bold />
          <Row label="Remaining RicePoints" value={money(done.remaining_balance)} />
        </div>
        <div className="mt-6 flex gap-3 justify-center">
          <Button asChild data-testid="success-orders-btn"><a href="/orders">View My Orders</a></Button>
          <Button variant="outline" asChild><a href="/ricepoints">My RicePoints</a></Button>
        </div>
      </div>
    );

  const place = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        items: payload, use_points: usePoints, payment_method: payment, address,
      });
      clear();
      setDone(data);
      toast.success("Order placed!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <h1 className="font-heading font-extrabold text-3xl tracking-tight">Checkout</h1>
      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-card border border-border rounded-lg p-5">
            <h2 className="font-heading font-bold">Delivery address</h2>
            <Textarea className="mt-3" rows={3} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="House no, street, area, Hyderabad" data-testid="checkout-address" />
          </div>
          <div className="bg-card border border-border rounded-lg p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coins className="text-primary" size={20} />
                <div>
                  <div className="font-heading font-bold">Use RicePoints</div>
                  <div className="text-xs text-muted-foreground">Up to 15% of eligible order value</div>
                </div>
              </div>
              <Switch checked={usePoints} onCheckedChange={setUsePoints} data-testid="use-points-switch" />
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-5">
            <h2 className="font-heading font-bold">Payment method</h2>
            <RadioGroup value={payment} onValueChange={setPayment} className="mt-3 space-y-2">
              <label className="flex items-center gap-3 border border-border rounded-md p-3 cursor-pointer" data-testid="pay-cod">
                <RadioGroupItem value="cod" /> <span>Cash on Delivery</span>
              </label>
              <label className="flex items-center gap-3 border border-border rounded-md p-3 cursor-pointer" data-testid="pay-online">
                <RadioGroupItem value="online" /> <span>Pay Online (demo — marked paid)</span>
              </label>
            </RadioGroup>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-5 h-fit lg:sticky lg:top-20">
          <h2 className="font-heading font-bold text-lg">Summary</h2>
          {quote && (
            <div className="mt-4 space-y-2 text-sm" data-testid="checkout-summary">
              <Row label="Order subtotal" value={money(quote.subtotal)} />
              <Row label="Discounts" value={money(quote.discount)} />
              <Row label="RicePoints balance" value={money(quote.ricepoints_balance)} />
              <Row label={`Max redemption (${quote.redemption_cap_pct}%)`} value={money(quote.max_redeemable)} />
              <Row label="RicePoints applied" value={`- ${money(quote.points_applied)}`} accent />
              <div className="border-t border-border my-2" />
              <Row label="Amount payable" value={money(quote.payable)} bold />
              <Row label="Remaining RicePoints" value={money(quote.remaining_balance)} />
            </div>
          )}
          <Button className="w-full mt-5" size="lg" onClick={place} disabled={placing} data-testid="place-order-btn">
            {placing ? "Placing…" : `Place Order · ${quote ? money(quote.payable) : ""}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, accent }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono-points ${bold ? "font-bold text-base" : "font-semibold"} ${accent ? "text-primary" : ""}`}>{value}</span>
    </div>
  );
}
